import type { RouteLocationNormalized, RouteRecordRaw, Router } from 'vue-router';
import EntityListPage from '@/pages/EntityListPage.vue';
import RecordPage from '@/pages/RecordPage.vue';
import { AREAS, entityGate, listPathFor } from '@/navigation/areas';
import { isGateOpen } from '@/navigation/gates';
import { useEntityStore } from '@/stores/entityStore';
import { useAuthStore } from '@/stores/authStore';

/**
 * One list route per entity, built from the area table. Each route carries its
 * entity in `meta.entityId`, and `selectEntityOfRoute` below copies it into the
 * entity store before the page shows. Column settings, saved views, export and bulk actions
 * already read the entity store, so they keep working unchanged.
 *
 * The paths are relative, because these are children of the `/` layout route.
 */
export function buildEntityListRoutes(): RouteRecordRaw[] {
    return AREAS.flatMap((area) =>
        area.entities.map((entity) => ({
            path: listPathFor(entity.entityId).slice(1),
            name: `list-${entity.entityId}`,
            component: EntityListPage,
            meta: { entityId: entity.entityId },
        })),
    );
}

/**
 * One record route per entity, for example /players/1. The id must be digits.
 * Record routes carry `meta.entityId` too, so the same guard refuses a record
 * of an entity the user cannot view.
 */
export function buildEntityRecordRoutes(): RouteRecordRaw[] {
    return AREAS.flatMap((area) =>
        area.entities.map((entity) => ({
            path: `${listPathFor(entity.entityId).slice(1)}/:id(\\d+)`,
            name: `record-${entity.entityId}`,
            component: RecordPage,
            meta: { entityId: entity.entityId },
        })),
    );
}

/**
 * Runs before any route that carries `meta.entityId`. It only checks the route.
 * It does not change the selected entity, because the navigation is not
 * confirmed yet. See `selectEntityOfRoute` for why.
 *
 * A user who cannot view the entity goes to the not-found page. Without that,
 * the entity store would quietly swap in the first entity the user can see, and
 * show it under the wrong address.
 */
export function guardEntityRoute(to: { meta: Record<string, unknown> }): true | { name: string } {
    const entityId = to.meta.entityId;
    if (typeof entityId !== 'string') {
        return true;
    }
    if (!isGateOpen(entityGate(entityId))) {
        return { name: 'not-found' };
    }
    return true;
}

/**
 * Copies the route's entity into the entity store once a navigation to it has
 * been confirmed.
 *
 * This must not happen any earlier. The page still on screen reads the selected
 * entity too. On Players with a saved view open, changing the entity early made
 * the Players list think its open view had changed. The list then cleared the
 * view from the address with `router.replace`, and that replace cancelled the
 * navigation the user had just started. A click on Clients, Music or Reports in
 * the icon strip did nothing.
 *
 * The router calls this straight after it switches to the new route, and before
 * Vue renders again. So the new page still sees its own entity when it sets
 * itself up, and the old page is unmounted without seeing it change.
 */
export function selectEntityOfRoute(to: { meta: Record<string, unknown> }): void {
    const entityId = to.meta.entityId;
    if (typeof entityId === 'string') {
        useEntityStore().setSelectedEntity(entityId);
    }
}

/** Registers the entity guard, and the entity selection that follows it, on the app's router. */
export function installEntityRouteGuards(router: Router): void {
    router.beforeEach(async (to: RouteLocationNormalized) => {
        if (typeof to.meta.entityId !== 'string') {
            return;
        }
        const authStore = useAuthStore();
        if (!authStore.userData) {
            await authStore.loadUser();
        }
        return guardEntityRoute(to);
    });
    router.afterEach((to, _from, navigationFailure) => {
        if (navigationFailure) {
            return;
        }
        selectEntityOfRoute(to);
    });
}
